import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  HStack,
  Text,
  Spinner,
  Icon,
  useDisclosure,
  useBreakpointValue,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  IconButton,
  Fade,
  ScaleFade,
  Button,
  Portal,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { FiSearch, FiEye, FiUsers } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { searchCustomers } from 'services/unifiedCustomersSearch';
import { normalizeCourseName, codeToDisplayLabel } from 'utils/courseUtils';
import ZoomableAvatar from 'components/Avatar/ZoomableAvatar.js';

const displayGroup = (raw) => raw ? codeToDisplayLabel(normalizeCourseName(raw)) : null;

const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => { clearTimeout(timeout); func(...args); };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const UserResultItem = ({ user, onClick, index = 0 }) => {
  const fullName = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Sin nombre';
  const hasAvatar = user.meta_data?.some(m => m.key === 'avatar_url' && m.value);
  const avatarSrc = hasAvatar
    ? `${process.env.REACT_APP_SURVEY_API_URL}/api/users/${user.username}/avatar-image`
    : null;

  return (
    <ScaleFade in={true} delay={index * 0.04}>
      <Box
        w="full"
        px={3}
        py="10px"
        cursor="pointer"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onClick(user)}
        transition="background 0.13s"
        borderTop="1px solid"
        borderColor="cream.line"
        _hover={{ bg: 'coral.tint' }}
        _active={{ bg: 'coral.soft' }}
        _first={{ borderTop: 'none' }}
      >
        <HStack spacing={3} w="full" minW={0}>
          <ZoomableAvatar
            size="sm"
            name={fullName}
            src={avatarSrc}
            bg="coral.500"
            color="white"
            flexShrink={0}
          />
          <Box flex={1} minW={0}>
            <Text fontSize="13.5px" fontWeight="700" color="ink.900" noOfLines={1} letterSpacing="-0.01em">
              {fullName}
            </Text>
            <HStack spacing={1} mt="1px">
              <Text fontSize="11.5px" color="ink.400" noOfLines={1} flex={1} minW={0}>
                {user.email || user.username || ''}
              </Text>
              {displayGroup(user.group) && (
                <Box
                  as="span"
                  px="7px"
                  py="1px"
                  borderRadius="999px"
                  bg="coral.soft"
                  color="coral.700"
                  fontSize="10.5px"
                  fontWeight="700"
                  flexShrink={0}
                  noOfLines={1}
                >
                  {displayGroup(user.group)}
                </Box>
              )}
            </HStack>
          </Box>
          <Icon as={FiEye} color="ink.400" boxSize="14px" flexShrink={0} />
        </HStack>
      </Box>
    </ScaleFade>
  );
};

const QuickUserSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const inputRef = useRef();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const history = useHistory();

  const handleSearch = async (term) => {
    if (term.length < 1) { setResults([]); return; }
    setIsSearching(true);
    try {
      const res = await searchCustomers(term, 8);
      setResults(res);
    } catch {
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const debouncedSearch = React.useCallback(debounce(handleSearch, 300), []);

  useEffect(() => {
    if (searchTerm.trim()) {
      debouncedSearch(searchTerm);
      if (!isOpen) onOpen();
    } else {
      setResults([]);
      if (isOpen) onClose();
    }
  }, [searchTerm]);

  const handleUserClick = (user) => {
    if (user.id) {
      history.push(`/admin/usuario/${user.id}`);
      setSearchTerm('');
      setResults([]);
      onClose();
      inputRef.current?.blur();
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setResults([]);
    onClose();
    inputRef.current?.focus();
  };

  const handleBlur = () => {
    setIsFocused(false);
    setTimeout(onClose, 150);
  };

  const isMobile = useBreakpointValue({ base: true, md: false });
  const shouldShowResults = isOpen && searchTerm.trim().length > 0;
  const hasResults = results.length > 0;

  const ResultsContent = () => (
    <Box fontFamily="jakarta">
      {isSearching ? (
        <HStack p={5} spacing={3} justify="center">
          <Spinner size="sm" color="coral.500" thickness="2px" />
          <Text fontSize="13px" color="ink.400">Buscando...</Text>
        </HStack>
      ) : hasResults ? (
        <Box>
          <HStack
            px={4} py="8px"
            bg="cream.bg2"
            justify="space-between"
            borderBottom="1px solid"
            borderColor="cream.line"
          >
            <Text fontSize="12px" fontWeight="700" color="ink.700" letterSpacing="0.02em">
              RESULTADOS
            </Text>
            <Text fontSize="11px" color="coral.500" fontWeight="700">
              {results.length}
            </Text>
          </HStack>
          <Box maxH="55vh" overflowY="auto" overflowX="hidden">
            {results.map((user, i) => (
              <UserResultItem
                key={`qs-${user.id}-${i}`}
                user={user}
                onClick={handleUserClick}
                index={i}
              />
            ))}
          </Box>
          <Box px={4} py="8px" bg="cream.bg2" borderTop="1px solid" borderColor="cream.line">
            <Button
              size="sm"
              variant="ghost"
              w="full"
              leftIcon={<FiUsers size="13px" />}
              color="ink.700"
              fontWeight="600"
              fontSize="12.5px"
              borderRadius="9px"
              _hover={{ bg: 'coral.soft', color: 'coral.700' }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { history.push('/admin/usuarios'); onClose(); setSearchTerm(''); }}
            >
              Ver todos los usuarios
            </Button>
          </Box>
        </Box>
      ) : searchTerm.trim() ? (
        <Box px={4} py={5} textAlign="center">
          <Text fontSize="13px" color="ink.400" mb={3}>
            Sin resultados para "{searchTerm}"
          </Text>
          <Button
            size="sm"
            bg="coral.500"
            color="white"
            borderRadius="9px"
            fontWeight="600"
            _hover={{ bg: 'coral.600' }}
            onClick={() => { history.push('/admin/usuarios'); onClose(); setSearchTerm(''); }}
          >
            Ver todos
          </Button>
        </Box>
      ) : null}
    </Box>
  );

  const inputEl = (
    <InputGroup size="sm" w="100%">
      <InputLeftElement height="full" pointerEvents="none">
        <Icon
          as={FiSearch}
          color={isFocused || searchTerm ? 'coral.500' : 'ink.400'}
          boxSize="14px"
          transition="color 0.15s"
        />
      </InputLeftElement>
      <Input
        ref={inputRef}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        placeholder="Buscar alumno..."
        bg="cream.bg"
        border="1.5px solid"
        borderColor={isFocused || searchTerm ? 'coral.500' : 'cream.line2'}
        borderRadius="11px"
        color="ink.700"
        fontWeight="500"
        fontSize="sm"
        _hover={{ borderColor: 'ink.400' }}
        _focus={{
          borderColor: 'coral.500',
          boxShadow: '0 0 0 1px var(--chakra-colors-coral-500)',
        }}
        _placeholder={{ color: 'ink.400', fontSize: 'sm' }}
        pr={searchTerm ? '32px' : '10px'}
      />
      {(isSearching || searchTerm) && (
        <InputRightElement height="full">
          {isSearching ? (
            <Spinner size="xs" color="coral.500" thickness="2px" />
          ) : (
            <IconButton
              size="xs"
              variant="ghost"
              icon={<CloseIcon w="8px" h="8px" />}
              onClick={handleClear}
              borderRadius="full"
              color="ink.400"
              _hover={{ bg: 'cream.bg2', color: 'coral.500' }}
            />
          )}
        </InputRightElement>
      )}
    </InputGroup>
  );

  return (
    <Box
      position="relative"
      display="block"
      flex={{ base: '1', md: 'none' }}
      maxW={{ base: 'none', md: '280px' }}
      minW={{ md: '200px' }}
      me={{ base: '8px', md: '16px' }}
    >
      {isMobile ? (
        <>
          {inputEl}
          {shouldShowResults && (
            <Portal>
              <Box
                position="fixed"
                top="60px"
                left="0"
                right="0"
                zIndex="1400"
                bg="white"
                borderTop="1px solid"
                borderColor="cream.line"
                boxShadow="0 8px 24px rgba(54,38,22,.12)"
              >
                <ResultsContent />
              </Box>
            </Portal>
          )}
        </>
      ) : (
        <Popover
          isOpen={shouldShowResults}
          onClose={onClose}
          placement="bottom-start"
          closeOnBlur={false}
          autoFocus={false}
          matchWidth
        >
          <PopoverTrigger>{inputEl}</PopoverTrigger>
          <Portal>
            <PopoverContent
              bg="white"
              border="1px solid"
              borderColor="cream.line"
              borderRadius="12px"
              boxShadow="0 8px 24px rgba(54,38,22,.12)"
              maxH="420px"
              overflow="hidden"
              zIndex="1500"
              _focus={{ outline: 'none' }}
            >
              <PopoverBody p={0}>
                <ResultsContent />
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      )}
    </Box>
  );
};

export default QuickUserSearch;
